//@ts-nocheck
import { Reducer, useReducer, useRef, useState } from "react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth } from "services/firebase";
import { AuthContext } from "./auth.context";
import { getCookie, isBrowser, removeCookie, setCookie } from "utils/session";
import { useSettings } from "contexts/settings/settings.context";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { clearFilter } from "redux/slices/shopFilter";
import { useQuery, useQueryClient } from "react-query";
import profileService from "services/profile";
import { IUser } from "interfaces/user.interface";
import { clearSearch } from "redux/slices/search";
import { selectCurrency } from "redux/slices/currency";
import { RecaptchaVerifier } from "firebase/auth";
import { clearFullCart } from "redux/slices/carts";
import { clearFullLiked } from "redux/slices/liked";
import { useRouter } from "next/router";

enum AuthActionKind {
  SIGN_IN = "SIGN_IN",
  LOGOUT = "LOGOUT",
}

interface AuthAction {
  type: AuthActionKind;
  payload: IUser;
}

function reducer(state: IUser, action: AuthAction): IUser | null {
  const { type, payload } = action;
  switch (type) {
    case AuthActionKind.SIGN_IN:
      setCookie("user", JSON.stringify(payload));
      return payload;
    case AuthActionKind.LOGOUT:
      removeCookie("access_token");
      setCookie("user", null);
      return null;
    default:
      return state;
  }
}

type Props = {
  children: any;
  authState: IUser;
};

export function AuthProvider({ children, authState }: Props) {
  const [user, authDispatch] = useReducer<Reducer<IUser, AuthAction>>(
    reducer,
    authState,
  );
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [walletHistoryOpen, setWalletHistoryOpen] = useState(false);
  const { resetSettings } = useSettings();
  const dispatch = useAppDispatch();
  const currency = useAppSelector(selectCurrency);
  const queryClient = useQueryClient();
  const router = useRouter();

  const isConfirmNeedForCloseModal = useRef(false);

  const isAuthenticated = Boolean(
    user?.id && (isBrowser ? getCookie("access_token") : true),
  );

  const { refetch } = useQuery(
    ["profile", currency?.id],
    () => profileService.get({ currency_id: currency?.id }),
    {
      enabled: isAuthenticated,
      onSuccess: (data) =>
        authDispatch({ type: AuthActionKind.SIGN_IN, payload: data.data }),
    },
  );

  function googleSignIn() {
    const googleAuthProvider = new GoogleAuthProvider();
    return signInWithPopup(auth, googleAuthProvider);
  }

  function facebookSignIn() {
    const facebookAuthProvider = new FacebookAuthProvider();
    return signInWithPopup(auth, facebookAuthProvider);
  }

  function appleSignIn() {
    const appleAuthProvider = new OAuthProvider("apple.com");
    return signInWithPopup(auth, appleAuthProvider);
  }

  function phoneNumberSignIn(phoneNumber) {
    const appVerifier = new RecaptchaVerifier(
      "sign-in-button",
      {
        size: "invisible",
        callback: () => {
          console.log("Callback!");
        },
      },
      auth,
    );
    return signInWithPhoneNumber(auth, phoneNumber, appVerifier);
  }

  function setUserData(data: any) {
    authDispatch({ type: AuthActionKind.SIGN_IN, payload: data });
  }

  function logout() {
    authDispatch({ type: AuthActionKind.LOGOUT });
    resetSettings();
    dispatch(clearFullCart());
    dispatch(clearFilter());
    dispatch(clearSearch());
    dispatch(clearFullLiked());
    queryClient.invalidateQueries(["notificationStatistic"], { exact: false });
    router.replace("/");
  }

  function openAuthModal() {
    setAuthModalOpen(true);
  }

  function closeAuthModal() {
    setAuthModalOpen(false);
  }

  function openWalletHistory() {
    setWalletHistoryOpen(true);
  }

  function closeWalletHistory() {
    setWalletHistoryOpen(false);
  }

  // write a decorator that accepts a function and checks if the user is authenticated before executing the function and if not, opens the auth modal.

  function withAuth(fn: Function) {
    return function (...args: any[]) {
      if (user) {
        return fn(...args);
      } else {
        openAuthModal();
      }
    };
  }

  return (
    <AuthContext.Provider
      value={{
        googleSignIn,
        facebookSignIn,
        appleSignIn,
        user,
        setUserData,
        isAuthenticated,
        logout,
        refetchUser: refetch,
        phoneNumberSignIn,
        openAuthModal,
        closeAuthModal,
        authModalOpen,
        withAuth,
        isConfirmNeedForCloseModal,
        walletHistoryOpen,
        openWalletHistory,
        closeWalletHistory,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
